/**
 * Toast Component
 *
 * Simple toast notification system.
 *
 * Story C-4.3 (Epic C-4 — Bezetting & conflicten): uitgebreid met een
 * optionele actieknop + instelbare duur (`ToastOptions`), voor de
 * "Indeling verwijderd — ongedaan maken"-undo-toast (8 s). Bewuste
 * afwijking van de story-tekst ("sonner") — zelfde precedent als
 * `AssignPopover.tsx` (Story C-3.3): dit project's `features/planning`
 * gebruikt overal al deze app-brede `useToast()`, geen tweede
 * toast-systeem introduceren voor één knop.
 */

import { useState, createContext, useContext, useCallback, ReactNode } from 'react';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  action?: ToastAction;
}

export interface ToastOptions {
  /** Duur in milliseconden vóór automatisch verdwijnen. Default 5000. */
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (message: string, type?: Toast['type'], options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: Toast['type'] = 'info', options?: ToastOptions) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type, action: options?.action }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, options?.duration ?? 5000);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: number) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const bgColors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500',
  };

  const icons = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  };

  // Test ID based on toast type
  const testId = toast.type === 'success' ? 'success-toast' :
                 toast.type === 'error' ? 'error-toast' :
                 'notification-toast';

  return (
    <div
      data-testid={testId}
      className={`${bgColors[toast.type]} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md animate-slide-in`}
      onClick={onRemove}
      role="alert"
    >
      <span className="text-lg">{icons[toast.type]}</span>
      <span className="flex-1">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          data-testid="toast-action"
          onClick={(e) => {
            e.stopPropagation();
            toast.action!.onClick();
            onRemove();
          }}
          className="font-semibold underline text-white hover:text-white/90"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="text-white/80 hover:text-white"
      >
        ✕
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
